import { createStore } from 'vuex';
import apiClient from '@/axios';
import router from '@/router';

const store = createStore({
  state: {
    user: JSON.parse(localStorage.getItem('user')) || null,
    token: localStorage.getItem('token') || '',
    roleLoaded: false,
    role: localStorage.getItem('role') || 'user',
    talents: [],
    projects: [],
    users: [],
    availableRoles: JSON.parse(localStorage.getItem('availableRoles')) || ['user'],
    notification: {
      show: false,
      type: '', // 'success', 'error', 'warning', 'info'
      message: '',
      duration: 5000
    }
  },
  getters: {
    isAdmin: (state) => state.role === 'admin',
    isManager: (state) => state.role === 'manager',
    isTalent: (state) => state.role === 'talent',
    isInvestor: (state) => state.role === 'investor',
    isUser: (state) => state.role === 'user',
    roleLoaded: (state) => state.roleLoaded,
    isAuthenticated: (state) => !!state.token && !!state.user,
    notification: (state) => state.notification,
    hasNotification: (state) => state.notification.show,
  },
  mutations: {
    setUser(state, user) {
      state.user = user;
      // Save all roles the user has
      if (user && user.role && Array.isArray(user.role)) {
        state.availableRoles = user.role;
      } else {
        state.availableRoles = ['user'];
      }
      localStorage.setItem('user', JSON.stringify(user));
      localStorage.setItem('availableRoles', JSON.stringify(state.availableRoles));
    },
    setToken(state, token) {
      state.token = token;
      localStorage.setItem('token', token);
    },
    setRole(state, role) {
      state.role = role;
      state.roleLoaded = true;
      localStorage.setItem('role', role);
    },
    clearUser(state) {
      state.user = null;
      state.token = '';
      state.roleLoaded = false;
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    },
    logout(state) {
      state.user = null;
      state.token = '';
      state.role = 'user';
      state.roleLoaded = false;
      state.talents = [];
      state.projects = [];
      state.users = [];
      state.availableRoles = ['user'];
    
      // Clear all localStorage items
      localStorage.removeItem('token');
      localStorage.removeItem('role');
      localStorage.removeItem('user');
      localStorage.removeItem('availableRoles');
    },
    setAvailableRoles(state, roles) {
      state.availableRoles = roles;
      localStorage.setItem('availableRoles', JSON.stringify(roles));
    },
    setRoleLoaded(state, loaded) {
      state.roleLoaded = loaded;
    },
    clearRoleData(state) {
      state.talents = [];
      state.projects = [];
      state.users = [];
    },
    setTalents(state, talents) {
      state.talents = talents;
    },
    setProjects(state, projects) {
      state.projects = projects;
    },
    setUsers(state, users) {
      state.users = users;
    },
    showNotification(state, { type, message, duration = 5000 }) {
      state.notification = {
        show: true,
        type,
        message,
        duration
      };
    },
    hideNotification(state) {
      state.notification = {
        show: false,
        type: '',
        message: '',
        duration: 5000
      };
    },
    // New mutation to sync state from localStorage
    syncFromLocalStorage(state) {
      const user = localStorage.getItem('user');
      const token = localStorage.getItem('token');
      const role = localStorage.getItem('role');
      const availableRoles = localStorage.getItem('availableRoles');

      state.user = user ? JSON.parse(user) : null;
      state.token = token || '';
      state.role = role || 'user';
      state.availableRoles = availableRoles ? JSON.parse(availableRoles) : ['user'];
      
      // Reset roleLoaded to false if user changed
      if (!state.user || !state.token) {
        state.roleLoaded = false;
        state.talents = [];
        state.projects = [];
        state.users = [];
      }
    }
  },
  actions: {
    async login({ commit }, credentials) {
      try {
        const response = await apiClient.post('api/token', credentials);
        commit('setToken', response.data.access_token);
        commit('setUser', response.data.user);

        if (response.data.user && response.data.user.role && response.data.user.role.length > 0) {
          commit('setRole', response.data.user.role[0]);
        } else {
          commit('setRole', 'user');
        }

        commit('setRoleLoaded', true);
        
        // Emit custom event for cross-tab communication
        window.dispatchEvent(new CustomEvent('auth-state-changed', { 
          detail: { type: 'login', user: response.data.user } 
        }));
        
        router.push({ name: 'UserList' });
      } catch (error) {
        console.error('Error logging in:', error);
        throw error;
      }
    },

    async logout({ commit }) {
      try {
        // Call logout API if you have one
        // await apiClient.post('/api/logout');
        
        commit('logout');
        
        // Emit custom event for cross-tab communication
        window.dispatchEvent(new CustomEvent('auth-state-changed', { 
          detail: { type: 'logout' } 
        }));
        
        router.push({ name: 'Login' });
      } catch (error) {
        console.error('Error logging out:', error);
        // Still perform local logout even if API call fails
        commit('logout');
        window.dispatchEvent(new CustomEvent('auth-state-changed', { 
          detail: { type: 'logout' } 
        }));
        router.push({ name: 'Login' });
      }
    },

    async switchRole({ commit, dispatch }, role) {
      // Set roleLoaded to false during transition
      commit('setRoleLoaded', false);

      // Clear previous role data
      commit('clearRoleData');

      // Set new role
      commit('setRole', role);

      // Fetch fresh data for the new role
      await dispatch('fetchRoleData');

      // Now that data is loaded, set roleLoaded to true
      commit('setRoleLoaded', true);
      
      // Emit event for role change
      window.dispatchEvent(new CustomEvent('auth-state-changed', { 
        detail: { type: 'role-switch', role } 
      }));
    },

    async fetchRoleData({ commit, state }) {
      try {
        // Don't fetch if not authenticated
        if (!state.token || !state.user) {
          return false;
        }

        // Fetch data based on current role
        if (state.role === 'manager' || state.role === 'admin') {
          const talentsResponse = await apiClient.get('/web/talents');
          commit('setTalents', talentsResponse.data.data);

          const projectsResponse = await apiClient.get('/web/projects_all');
          commit('setProjects', projectsResponse.data.data);

          if (state.role === 'admin') {
            const usersResponse = await apiClient.get('web/users');
            commit('setUsers', usersResponse.data.data);
          }
        } else if (state.role === 'investor') {
          // Fetch investor-specific data
          const investorProjectsResponse = await apiClient.get('/web/projects/Investor/all');
          commit('setProjects', investorProjectsResponse.data.data);
        } else if (state.role === 'talent') {
          // Fetch talent-specific data          
          const talentProjectsResponse = await apiClient.get('/web/talent/me');
          const talentData = talentProjectsResponse.data.data;
          commit('setProjects', talentData.projects || talentData);
        }
        return true;
      } catch (error) {
        console.error('Error fetching role data:', error);
        // If API call fails due to auth issues, logout
        if (error.response && (error.response.status === 401 || error.response.status === 403)) {
          commit('logout');
          window.dispatchEvent(new CustomEvent('auth-state-changed', { 
            detail: { type: 'logout' } 
          }));
        }
        return false;
      }
    },

    async initializeStore({ commit, dispatch, state }) {
      // First, sync from localStorage in case another tab made changes
      commit('syncFromLocalStorage');
      
      // Only fetch data if we have a valid user and token
      if (state.user && state.token) {
        const success = await dispatch('fetchRoleData');
        if (success) {
          commit('setRoleLoaded', true);
        }
      } else {
        commit('setRoleLoaded', true); // Set to true even if not authenticated
      }
    },

    // New action to handle cross-tab synchronization
    handleStorageChange({ commit, dispatch }, event) {
      if (event.key === 'user' || event.key === 'token' || event.key === 'role' || event.key === 'availableRoles') {
        commit('syncFromLocalStorage');
        // Re-initialize if needed
        dispatch('initializeStore');
      }
    },
    showErrorNotification({ commit }, message) {
      commit('showNotification', { type: 'error', message });
    },
    
    showSuccessNotification({ commit }, message) {
      commit('showNotification', { type: 'success', message });
    },
    
    showWarningNotification({ commit }, message) {
      commit('showNotification', { type: 'warning', message });
    },
    
    showInfoNotification({ commit }, message) {
      commit('showNotification', { type: 'info', message });
    },
    
    clearNotification({ commit }) {
      commit('hideNotification');
    }
  }
});

// Set up cross-tab communication listeners
if (typeof window !== 'undefined') {
  // Listen for localStorage changes from other tabs
  window.addEventListener('storage', (event) => {
    store.dispatch('handleStorageChange', event);
  });

  // Listen for custom auth events from other tabs
  window.addEventListener('auth-state-changed', (event) => {
    console.log('Auth state changed in another tab:', event.detail);
    store.commit('syncFromLocalStorage');
    store.dispatch('initializeStore');
  });

  // Handle page visibility changes (when tab becomes active)
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) {
      // Tab became visible, sync state
      store.commit('syncFromLocalStorage');
      store.dispatch('initializeStore');
    }
  });

  // Handle window focus (additional safety net)
  window.addEventListener('focus', () => {
    store.commit('syncFromLocalStorage');
    store.dispatch('initializeStore');
  });
}

export default store;